import cn from 'classnames';
import { format } from 'date-fns';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Image, Link } from 'components';
import Chip from 'components/Chip';
import { DatePosted } from 'components/DatePosted/DatePosted.component';
import Deadline from 'components/Deadline';
import Salary from 'components/Salary';
import CardLogo from 'components/candidate/CardLogo';
import { Row } from 'components/layout';

import { WorkingModel } from 'lib/models/job';
import { TouchpointType } from 'lib/models/touchpoint';
import { REGEX } from 'lib/utils/regex';
import { isSafari } from 'utils/dom';
import { getRandomInt, getRandomProfiles, randomBGColor } from 'utils/format';

import styles from './TouchpointCard.module.scss';

export interface TouchpointCartDataProps {
  id: string;
  title: string;
  application_deadline: string;
  maximum_salary: number;
  minimum_salary: number;
  pay_currency: string;
  touchpointable_type: TouchpointType;
  isExpired: boolean;
  url: string;
  kind: string;
  working_model: WorkingModel;
  area_of_responsibilities?: string;
  created_at: string;
  company?: {
    name: string;
    logo: string;
    is_verified: boolean;
    huzzle_verified: boolean;
    showLogo?: boolean;
  };
  sideBanner?: string;
  isEventPage?: boolean;
  start_date?: string;
  avatar_urls: Array<string>;
  applications?: number;
}

export interface TouchpointCardProps extends TouchpointCartDataProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** title of the opportunity */
}

export const TouchpointCard: React.FC<TouchpointCardProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    title,
    maximum_salary,
    minimum_salary,
    pay_currency,
    application_deadline,
    touchpointable_type,
    isExpired,
    url,
    kind,
    working_model,
    area_of_responsibilities,
    created_at,
    company,
    sideBanner,
    isEventPage = false,
    start_date,
    avatar_urls,
    applications,
  }: TouchpointCardProps) => {
    const contentClassNames = cn(
      {
        [styles.TouchpointCard]: true,
        [styles.TouchpointCardEvent]: true,
      },
      className
    );

    const [t] = useTranslation('onboarding.recruiter');

    const { showLogo = true } = company || {};
    const startDateValue = start_date
      ? format(new Date(start_date), 'MMM d, h:mma')
      : null;

    const getEmoji = (emoji: string, index: number) => {
      const randomNumber = getRandomInt(0, 10, true);
      const color = randomBGColor[randomNumber as number];
      return (
        <div key={index} className={styles.randomprofileBorder}>
          <p
            style={{ backgroundColor: color }}
            className={cn(styles.randomprofile, { [styles.safari]: isSafari })}
          >
            {emoji}
          </p>
        </div>
      );
    };
    const getProfilePic = (url: string, index: number) => {
      return (
        <div key={index} className={styles.randomprofileBorder}>
          <Image
            width={27}
            height={27}
            src={url}
            source={undefined}
            className={styles.img}
          />
        </div>
      );
    };

    const applicantsRow = useMemo(() => {
      if (applications) {
        const leftApplicantsNumber = applications - 10;
        const profiles = getRandomProfiles(10, avatar_urls || []);

        return (
          <div className={styles.applicantsRow}>
            <div className={styles.applicantsRowImg}>
              {profiles.map((item, index) => {
                if (REGEX.URL_CHECK.test(item)) {
                  return getProfilePic(item, index);
                } else {
                  return getEmoji(item, index);
                }
              })}
              {leftApplicantsNumber > 0 ? (
                <div className={styles.numberBorder}>
                  <p className={styles.number}>+{leftApplicantsNumber}</p>
                </div>
              ) : null}
            </div>
            <p className={styles.desc}>
              {t('step-1.touchpoint-card.applications-stats', { applications })}
            </p>
          </div>
        );
      }
    }, [applications, avatar_urls]);
    return (
      <Link
        href={url}
        underlineOnHover={false}
        className={styles.link}
        target="_blank"
      >
        <div
          className={contentClassNames}
          style={style}
          data-testid="TouchpointCard"
        >
          {company && showLogo ? (
            <CardLogo
              is_verified={company?.is_verified}
              huzzle_verified={company?.huzzle_verified}
              logo_url={company?.logo}
              initials={company?.name.substring(0, 1).toUpperCase()}
              size={'large'}
              className={styles.logo}
              verifiedClassName={styles.icon}
              cohostsLogoDetails={null}
            />
          ) : null}
          <div className={styles.main}>
            <Row justify="space-between" wrap={false} columnGap={12}>
              <div>
                {isEventPage ? (
                  <p className={styles.startDate}>{startDateValue}</p>
                ) : null}
                <p
                  className={cn(styles.title, {
                    [styles.titleEvent]: isEventPage,
                  })}
                >
                  {title}
                </p>

                <Row className={styles.detailRow}>
                  <div>
                    {touchpointable_type}

                    {company ? (
                      <>
                        <span className={styles.dot}>•</span>
                        {company.name}
                      </>
                    ) : (
                      <>
                        {area_of_responsibilities ? (
                          <>
                            <span className={styles.dot}>•</span>
                            {area_of_responsibilities}
                          </>
                        ) : null}{' '}
                      </>
                    )}
                    {created_at ? (
                      <>
                        <span className={styles.dot}>•</span>
                        <DatePosted
                          created_at={created_at}
                          className={styles.datePosted}
                        />
                      </>
                    ) : null}
                  </div>
                </Row>
              </div>

              {sideBanner && (
                <Image src={sideBanner} className={styles.sideIcon} />
              )}
            </Row>
            {minimum_salary ||
            maximum_salary ||
            application_deadline ||
            kind ||
            working_model ? (
              <Row align="center" className={styles.detail}>
                {kind ? (
                  <Chip
                    className={cn(styles.chip, {
                      [styles.eventChip]: isEventPage,
                    })}
                  >
                    {touchpointable_type === TouchpointType.Job
                      ? `💼 ${kind}`
                      : `🚀 ${kind}`}
                  </Chip>
                ) : null}
                {minimum_salary || maximum_salary ? (
                  <Salary
                    minimum_salary={minimum_salary}
                    maximum_salary={maximum_salary}
                    pay_currency={pay_currency}
                    className={cn(styles.salary, styles.chip, {
                      [styles.eventChip]: isEventPage,
                    })}
                  />
                ) : null}
                {working_model === WorkingModel.REMOTE ? (
                  <Chip
                    className={cn(styles.chip, {
                      [styles.eventChip]: isEventPage,
                    })}
                  >
                    💻 {working_model}
                  </Chip>
                ) : null}
                {!isExpired && application_deadline && (
                  <Deadline
                    application_deadline={application_deadline}
                    className={cn(styles.chipDeadline, {
                      [styles.eventChip]: isEventPage,
                    })}
                  />
                )}
              </Row>
            ) : null}
            {isEventPage && applications && applications >= 10
              ? applicantsRow
              : null}
          </div>
        </div>
      </Link>
    );
  }
);

TouchpointCard.displayName = 'TouchpointCard';
